import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { GOOGLE_API_URL } from '@/config';
import { UserInfoRequest, UserInfoResponse } from './models';

const DEFAULT_CONFIG: AxiosRequestConfig = {
  baseURL: GOOGLE_API_URL,
};

class GoogleAPI {
  private readonly theAxios: AxiosInstance;

  private static instance: GoogleAPI;

  private constructor(config: AxiosRequestConfig) {
    this.theAxios = axios.create(config);
    this.initResponseInterceptors();
  }

  private initResponseInterceptors = (): void => {
    this.theAxios.interceptors.response.use(
      this.handleSuccessResponse,
      this.handleErrorResponse,
    );
  };

  private handleSuccessResponse = (response: AxiosResponse) => response;

  private handleErrorResponse = (error: AxiosError) => Promise.reject(error);

  public static getInstance(
    config: AxiosRequestConfig = DEFAULT_CONFIG,
  ): GoogleAPI {
    if (!this.instance) {
      this.instance = new GoogleAPI(config);
    }
    return this.instance;
  }

  private post = <S, T>(
    endpoint: string,
    data: S,
    config?: AxiosRequestConfig<any>,
  ) => {
    return this.theAxios
      .post<T>(endpoint, data, config)
      .then((response: AxiosResponse<T>) => response.data);
  };

  private put = <S, T>(endpoint: string, data: S) => {
    return this.theAxios
      .put<T>(endpoint, data)
      .then((response: AxiosResponse<T>) => response.data);
  };

  private patch = <S, T>(endpoint: string, data: S) => {
    return this.theAxios
      .patch<T>(endpoint, data)
      .then((response: AxiosResponse<T>) => response.data);
  };

  private get = <S, T>(
    endpoint: string,
    data: S,
    config: AxiosRequestConfig = {},
  ) => {
    return this.theAxios
      .get<T>(endpoint, { params: data, ...config })
      .then((response: AxiosResponse<T>) => response.data);
  };

  private delete = <T>(endpoint: string) => {
    return this.theAxios.delete<T>(endpoint);
  };

  public getUserInfo = (data: { token_type: string; access_token: string }) => {
    const payload = {};
    const authorizationHeader = `${data.token_type} ${data.access_token}`;

    return this.get<UserInfoRequest, UserInfoResponse>(
      '/oauth2/v3/userinfo',
      payload,
      {
        headers: {
          Authorization: authorizationHeader,
        },
      },
    );
  };
}

const googleAPI = GoogleAPI.getInstance();

export default googleAPI;
